import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { NotificationItem } from '@/hooks/common/useNotifications';
import { RESIDENTS_DESKTOP_COLORS } from '@/constants/residents/desktop/color';
import { strings } from '@/constants/residents/strings';
import { NotificationAvatar } from './mobile/NotificationAvatar';

type Props = {
  item: NotificationItem;
  onPress: () => void;
  isSelected?: boolean;
  showDateSmall?: boolean;
  rightAccessory?: React.ReactNode;
  unread?: boolean;
  dateText?: string;
  compact?: boolean;
  titleStyleOverride?: any;
  rightWidth?: number;
};

export const NotificationRow: React.FC<Props> = ({ item, onPress, isSelected, showDateSmall, rightAccessory, unread, dateText, compact, titleStyleOverride, rightWidth }) => {
  // Check if this is a marketplace notification
  const isMarketplaceFeedback = item.notificationType === 'marketplace_feedback';
  const isMarketplaceNotification = item.notificationType === 'marketplace_notification';
  const isMarketplace = isMarketplaceFeedback || isMarketplaceNotification;
  
  // Check if this is a property chat notification
  const isPropertyChat = item.notificationType === 'property_chat';
  
  // Get listing image for marketplace notifications
  const listingImageUrl = isMarketplaceFeedback 
    ? item.feedbackRequest?.listing?.image 
    : isMarketplaceNotification 
      ? item.marketplaceNotification?.metadata?.listingImageUrl 
      : undefined;
  
  // Determine avatar variant based on notification type
  const avatarVariant = isPropertyChat 
    ? 'message' 
    : isMarketplace 
      ? 'marketplace' 
      : 'property';

  return (
    <View style={[styles.row, compact && styles.rowCompact, isSelected && styles.rowSelected]}>
      <TouchableOpacity onPress={onPress} style={styles.rowPress} accessibilityRole="button">
        <View style={styles.rowLeft}>
          <NotificationAvatar
            showUnreadDot={unread}
            variant={avatarVariant}
            imageUrl={listingImageUrl}
            platform="desktop"
          />
        </View>
        <View style={styles.rowTextWrap}>
          {!!showDateSmall && !!dateText && <Text style={styles.rowDateSmall}>{dateText}</Text>}
          <Text numberOfLines={1} style={[styles.rowTitle, titleStyleOverride]}>{item.title || item.responseDescription || strings.notifications.desktop.defaultTitle}</Text>
          <Text numberOfLines={2} style={styles.rowSub}>{item.description || item.descriptionHtml || ''}</Text>
        </View>
      </TouchableOpacity>
      {!!rightAccessory && (
        <View style={[styles.rowRight, rightWidth !== undefined ? { width: rightWidth } : null]}>
          {rightAccessory}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 18,
    backgroundColor: RESIDENTS_DESKTOP_COLORS.SECONDARY_BACKGROUND,
  },
  rowCompact: { paddingVertical: 10 },
  rowPress: { flexDirection: 'row', alignItems: 'center', flex: 1 },
  rowSelected: {
    borderWidth: 1,
    borderColor: RESIDENTS_DESKTOP_COLORS.BORDER_PRIMARY,
  },
  rowLeft: { paddingRight: 8 },
  rowTextWrap: { flex: 1, paddingRight: 8 },
  rowDateSmall: { color: RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY, fontSize: 10, marginBottom: 4 },
  rowTitle: { color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY, fontWeight: '700', fontSize: 16 },
  rowSub: { color: RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY, fontSize: 14, marginTop: 2 },
  rowRight: { width: 64, alignItems: 'flex-end' },
});

export default NotificationRow;


